import { audience, baseUrl } from 'api/helpers'
import axios from 'axios'
import { isEmpty } from 'lodash'
import { getVisitToken, getVisitorToken } from 'api/ahoy'
// TODO: Handle camelCase and snakeCase conversions in the API
// Rails API: convert to snake_case prior to sending
// Frontend: convert to camelCase after receiving
// import { toCamelCase, toSnakeCase } from 'api/helpers'

const buildHeaders = (token, contentType = 'application/json') => {
  const headers = { 'Content-Type': contentType, Authorization: `Bearer ${token}` }
  const visitToken = getVisitToken()
  const visitorToken = getVisitorToken()
  if (visitToken) headers['Ahoy-Visit'] = visitToken
  if (visitorToken) headers['Ahoy-Visitor'] = visitorToken
  return headers
}

const buildCleanParams = (params) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }
    return acc
  }, {})
}

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v)

const buildQueryString = (cleanParams) => {
  const searchParams = new URLSearchParams()
  Object.entries(cleanParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v, idx) => {
        if (isPlainObject(v)) {
          // Indexed array-of-objects: key[idx][nested]=value
          Object.entries(v).forEach(([nestedKey, nestedValue]) => {
            if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
              searchParams.append(`${key}[${idx}][${nestedKey}]`, nestedValue)
            }
          })
        } else {
          // Scalar array: key[]=value
          searchParams.append(`${key}[]`, v)
        }
      })
    } else {
      searchParams.append(key, value)
    }
  })
  return searchParams
}

export const apiGet = async ({ url, token, params = {} }) => {
  const fullUrl = new URL(`${baseUrl}/${url}`)
  const cleanParams = buildCleanParams(params)

  if (!isEmpty(cleanParams)) {
    fullUrl.search = buildQueryString(cleanParams).toString()
  }

  return axios
    .get(fullUrl, { audience, headers: buildHeaders(token) })
    .then((response) => {
      return response.data
    })
}

// POST-based query for endpoints that support it, used when params are too large for GET query strings.
// Sends the same params as a JSON body instead of query string.
export const apiPostQuery = async ({ url, token, params = {} }) => {
  const fullUrl = `${baseUrl}/${url}`
  const cleanParams = buildCleanParams(params)

  return axios
    .post(fullUrl, cleanParams, { audience, headers: buildHeaders(token) })
    .then((response) => {
      return response.data
    })
}

// Estimates query string length for the given params
export const estimateQueryStringLength = (params) => {
  const cleanParams = buildCleanParams(params)
  if (isEmpty(cleanParams)) return 0
  return buildQueryString(cleanParams).toString().length
}

export const apiPatch = async ({ url, token, objectName, params = {} }) => {
  const fullUrl = `${baseUrl}/${url}`
  const jsonParams = JSON.stringify({ [objectName]: params })

  const data = await axios
    .patch(fullUrl, jsonParams, { audience, headers: buildHeaders(token) })
    .then((response) => {
      return response.data
    })

  return data
}

export const apiPost = ({ url, token, objectName, params = {} }) => {
  const fullUrl = `${baseUrl}/${url}`
  const jsonParams = JSON.stringify({ [objectName]: params })

  return axios
    .post(fullUrl, jsonParams, { audience, headers: buildHeaders(token) })
    .then((response) => {
      return response.data
    })
}

export const apiDelete = async ({ url, token }) => {
  const fullUrl = `${baseUrl}/${url}`

  const data = await axios
    .delete(fullUrl, { audience, headers: buildHeaders(token) })
    .then((response) => {
      return response.data
    })

  return data
}

export const apiPatchFormData = async ({ url, token, objectName, params = {} }) => {
  const fullUrl = `${baseUrl}/${url}`
  const formData = new FormData()

  // Append all params to FormData, nested under objectName
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(`${objectName}[${key}]`, value)
    }
  })

  const data = await axios
    .patch(fullUrl, formData, { audience, headers: buildHeaders(token, 'multipart/form-data') })
    .then((response) => {
      return response.data
    })

  return data
}
